/**
 * preferencesActions.js
 * Talkroute
 *
 * Created by Vel Genov on 12/16/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */
import { ErrorStatusCode } from "@helpers/hooks/useService.hook";
import { handleAuthorizationErrors } from "@helpers/thunkHelpers";
import { RowsPerPageOptions } from "@models/AppPreference.models";
import { IPreferencesTableSize } from "@reducers/preferencesReducer";
import moment from "moment";

import validateArray from "../helpers/arrayValidator";
import apiServiceInstance from "../services/TalkrouteAPIService";

export const AUTO_UPDATE_PERMISSION_CHANGED = "AUTO_UPDATE_PERMISSION_CHANGED";
export const FETCH_PHONE_NUMBERS_BEGIN = "FETCH_PHONE_NUMBERS_BEGIN";
export const FETCH_PHONE_NUMBERS_SUCCESS = "FETCH_PHONE_NUMBERS_SUCCESS";
export const FETCH_PHONE_NUMBERS_FAILURE = "FETCH_PHONE_NUMBERS_FAILURE";
export const UPDATE_APP_NOW = "UPDATE_APP_NOW";
export const CLEAR_UPDATE_STATE = "CLEAR_UPDATE_STATE";
export const TOGGLE_TEST_MIC = "TOGGLE_TEST_MIC";
export const SET_DEFAULT_AUDIO_INPUT = "SET_DEFAULT_AUDIO_INPUT";
export const SET_DEFAULT_AUDIO_OUTPUT = "SET_DEFAULT_AUDIO_OUTPUT";
export const SET_TEXT_NOTIFICATION_SOUND = "SET_TEXT_NOTIFICATION_SOUND";
export const SET_CALL_NOTIFICATION_SOUND = "SET_CALL_NOTIFICATION_SOUND";
export const SET_PREFERENCES_TABLE_SIZE = "SET_PREFERENCES_TABLE_SIZE";
export const SET_AUDIO_OUTPUT_VOLUME = "SET_AUDIO_OUTPUT_VOLUME";
export const SET_AUDIO_INPUT_VOLUME = "SET_AUDIO_INPUT_VOLUME";
export const SET_OPEN_PREFERENCES = "SET_OPEN_PREFERENCES"; 
export const SET_OPEN_HISTORY_REPORTING = "SET_OPEN_HISTORY_REPORTING"
export const TOGGLE_VISIBILITY = "TOGGLE_VISIBILITY";
export const TOGGLE_HISTORY_REPORTING_VISIBILITY = "TOGGLE_HISTORY_REPORTING_VISIBILITY";
export const OUTGOING_PHONE_NUMBER_SELECTED = "OUTGOING_PHONE_NUMBER_SELECTED";
export const OUTGOING_TEXT_PHONE_NUMBER_SELECTED =
  "OUTGOING_TEXT_PHONE_NUMBER_SELECTED";
export const IS_AGREED_TERMS_CHANGED = "IS_AGREED_TERMS_CHANGED";
export const AUTO_UPDATE_CHECKBOX = "AUTO_UPDATE_CHECKBOX";
export const SPELL_CHECK_CHECKBOX = "SPELL_CHECK_CHECKBOX";
export const DEFAULT_AUDIO_SOURCE = "default";
export const CHECKED_FOR_UPDATES = "CHECKED_FOR_UPDATES";
export const UPDATE_TABLE_ROWS_PER_PAGE_DEFAULT = "UPDATE_TABLE_ROWS_PER_PAGE_DEFAULT";

/**
 * Permissions to automatically update the application have changed
 * @param isAutoUpdateAllowed a Boolean variable that shows if auto update is allowed by the user
 */
export const settingChanged = (key, value) => ({
  type: key,
  payload: {
    value,
  },
});

/**
 * Gets the Talkroute phone numbers from a web service. Currently using a mock service
 */
export const fetchPhoneNumbers = () => {
  return (dispatch, getState) => {
    dispatch(fetchPhoneNumbersBegin());
    return apiServiceInstance
      .getPhoneNumbers()
      .then((data) => {
        const outgoingPhoneNumbers = getOutgoingPhoneNumbers(data);
        const outgoingTextPhoneNumbers = getOutgoingTextPhoneNumbers(data);
        const allPhoneNumbers = getAllPhoneNumbers(data);

        // Sort outgoing text numbers such that editable and enabled are at the top
        const sortedOutgoingTextNumbers = outgoingTextPhoneNumbers.sort((a, b) => {
          const aIsEditableEnabled = a.getTextMessagingAllowed() && a.getTextMessagingEnabled();
          const bIsEditableEnabled = b.getTextMessagingAllowed() && b.getTextMessagingEnabled();

          if (aIsEditableEnabled && !bIsEditableEnabled) {
            return -1;
          }
          if (!aIsEditableEnabled && bIsEditableEnabled) {
            return 1;
          }
          return 0;
        });

        if (
          validateArray(outgoingPhoneNumbers) &&
          outgoingPhoneNumbers.length === 1
        ) {
          // If there is only 1 outgoing phone number, select it
          const selectedPhoneNumber = outgoingPhoneNumbers[0];
          dispatch(
            outgoingPhoneNumberSelected(selectedPhoneNumber?.phoneNumber)
          );
        }

        if (
          validateArray(outgoingTextPhoneNumbers) &&
          outgoingTextPhoneNumbers.length === 1
        ) {
          // If there is only 1 outgoing text phone number, select it
          const selectedPhoneNumber = outgoingTextPhoneNumbers[0];
          dispatch(
            outgoingTextPhoneNumberSelected(selectedPhoneNumber?.phoneNumber)
          );
        }

        // set defaults if current selected phones doesn't exist
        const {
          selectedOutgoingPhoneNumber,
          selectedOutgoingTextNumber,
        } = getState().preferences;

        if (
          !selectedOutgoingPhoneNumber ||
          !outgoingPhoneNumbers.find(
            (item) => item.phoneNumber === selectedOutgoingPhoneNumber
          )
        ) {
          dispatch(
            outgoingPhoneNumberSelected(outgoingPhoneNumbers[0]?.phoneNumber)
          );
        }

        if (
          !selectedOutgoingTextNumber ||
          !outgoingTextPhoneNumbers.find(
            (item) => item.phoneNumber === selectedOutgoingTextNumber
          )
        ) {
          dispatch(
            outgoingTextPhoneNumberSelected(
              sortedOutgoingTextNumbers[0]?.phoneNumber
            )
          );
        }

        dispatch(
          fetchPhoneNumbersSuccess({
            outgoingPhoneNumbers,
            outgoingTextPhoneNumbers,
            allPhoneNumbers,
          })
        );
        return data;
      })
      .catch((error) => {
        if (error.originalError && error.originalError.isAxiosError) {
          error.originalError.response?.status === ErrorStatusCode.UNAUTHORIZED &&
          handleAuthorizationErrors(dispatch, getState().authentication)
        }
        dispatch(fetchPhoneNumbersFailure(error.errorParsed))
      });
  };
};

/**
 * A local function to get all of the outgoing phone numbers
 *
 * @param {*} phoneNumbers an array of phone numbers
 * @returns an array of the outgoing phone numbers only
 */
const getAllPhoneNumbers = (phoneNumbers) => {
  let allPhoneNumbers: any[] = [];

  if (validateArray(phoneNumbers)) {
    allPhoneNumbers = phoneNumbers;
  }
  return allPhoneNumbers;
};

/**
 * A local function to filter out the outgoing phone numbers from an array
 * of phone numbers
 *
 * @param {*} phoneNumbers an array of phone numbers
 * @returns an array of the outgoing phone numbers only
 */
const getOutgoingPhoneNumbers = (phoneNumbers) => {
  let outgoingPhoneNumbers: any[] = [];

  if (validateArray(phoneNumbers)) {
    outgoingPhoneNumbers = phoneNumbers.filter(
      (entry) => entry.getVoiceEnabled() === true
    );
  }
  return outgoingPhoneNumbers;
};

/**
 * A local function to filter out the outgoing text phone numbers from an array
 * of phone numbers
 *
 * @param {*} phoneNumbers an array of phone numbers
 * @returns an array of the outgoing text phone numbers only
 */
const getOutgoingTextPhoneNumbers = (phoneNumbers) => {
  let outgoingTextPhoneNumbers: any[] = [];

  if (validateArray(phoneNumbers)) {
    outgoingTextPhoneNumbers = phoneNumbers.filter(
      (entry) => entry.getTextMessagingEnabled()
    );
  }
  return outgoingTextPhoneNumbers;
};

export const setOpenPreferences = (isOpened: boolean) => (dispatch) => {
  dispatch({ type: SET_OPEN_PREFERENCES, payload: isOpened })
};

export const setOpenHistoryAndReporting = (isOpened: boolean) => (dispatch) => {
  dispatch({ type: SET_OPEN_HISTORY_REPORTING, payload: isOpened })
};

/**
 * An action sent to update the application
 */
export const updateAppNow = () => ({
  type: UPDATE_APP_NOW,
});

/**
 * An action to clear the request to update the application
 */
export const clearUpdateNowState = () => ({
  type: CLEAR_UPDATE_STATE,
});

/**
 * The application finished checking for updates
 */
export const checkedForUpdates = () => {
  const timestamp = moment().unix();

  return {
    type: CHECKED_FOR_UPDATES,
    payload: timestamp,
  };
};

/**
 * Successfully fetched call history
 * @param {*} callHistory the call history array that was fetched
 */
export const fetchPhoneNumbersBegin = () => ({
  type: FETCH_PHONE_NUMBERS_BEGIN,
});

/**
 * Successfully fetched call history
 * @param {*} callHistory the call history array that was fetched
 */
export const fetchPhoneNumbersSuccess = (data) => ({
  type: FETCH_PHONE_NUMBERS_SUCCESS,
  payload: { ...data },
});

/**
 * Sets the number of call history rows to fetch in a table
 * @param payload 
 */
 export const updateRowsPerPageSetting = (payload: RowsPerPageOptions) => ({
  type: UPDATE_TABLE_ROWS_PER_PAGE_DEFAULT,
  payload
})

/**
 * An error occured while fetching call history
 * @param {*} error
 */
export const fetchPhoneNumbersFailure = (error) => ({
  type: FETCH_PHONE_NUMBERS_FAILURE,
  payload: { error },
});

/**
 * Sets the Test Mic enabled or not
 * @param {*} isEnabled
 */
export const toggleTestMic = (isEnabled) => ({
  type: TOGGLE_TEST_MIC,
  payload: { isEnabledTestMic: isEnabled },
});

/**
 * Sets the default input id the to one passed in
 *
 * Note that this function only sets the Redux value.
 * It's up to the audio components if they will use this value
 * @param {*} input the user prefered input id
 */
export const setDefaultAudioInput = (input) => ({
  type: SET_DEFAULT_AUDIO_INPUT,
  payload: { defaultAudioInput: input },
});

/**
 * Sets the default output id the to one passed in
 *
 * Note that this function only sets the Redux value.
 * It's up to the audio components if they will use this value
 * @param {*} output the user prefered output id
 */
export const setDefaultAudioOutput = (output) => ({
  type: SET_DEFAULT_AUDIO_OUTPUT,
  payload: { defaultAudioOutput: output },
});

/**
 * Sets the text notification sound
 * @param {*} num the number of notification sound
 */
export const setTextNotificationSound = (num: number) => ({
  type: SET_TEXT_NOTIFICATION_SOUND,
  payload: { notificationSound: num },
});

/**
 * Sets the call notification sound
 * @param {*} num the number of notification sound
 */
export const setCallNotificationSound = (num: number) => ({
  type: SET_CALL_NOTIFICATION_SOUND,
  payload: { notificationSound: num },
});

/**
 * Sets the audio ouput volume
 * @param {*} volume the volume in percents
 */
export const setAudioOutputVolume = (volume) => ({
  type: SET_AUDIO_OUTPUT_VOLUME,
  payload: { audioOutputVolume: volume },
});

/**
 * Sets the audio input volume
 * @param {*} volume the volume in percents
 */
export const setAudioInputVolume = (volume) => ({
  type: SET_AUDIO_INPUT_VOLUME,
  payload: { audioInputVolume: volume },
});

/**
 * Sets the visibility state of the preferences parameter
 *
 * @param {*} isOpen a boolean to indicate visibility
 */
export const togglePreferencesWindow = (isOpen) => ({
  type: TOGGLE_VISIBILITY,
  payload: { isPreferencesVisible: isOpen },
});

/**
 * Sets the visibility state of the history and reporting parameter
 *
 * @param {*} isOpen a boolean to indicate visibility
 */
export const toggleHistoryAndReportingWindow = (isOpen) => ({
  type: TOGGLE_HISTORY_REPORTING_VISIBILITY,
  payload: { isHistoryAndReportingVisible: isOpen },
});

/**
 * Sets the default voice phone number
 *
 * @param {*} phoneNumber the default phone number
 */
export const outgoingPhoneNumberSelected = (phoneNumber) => ({
  type: OUTGOING_PHONE_NUMBER_SELECTED,
  payload: { value: phoneNumber },
});

/**
 * Sets the default text messaging phone number
 *
 * @param {*} phoneNumber the default text messaging phone number
 */
export const outgoingTextPhoneNumberSelected = (phoneNumber) => ({
  type: OUTGOING_TEXT_PHONE_NUMBER_SELECTED,
  payload: { value: phoneNumber },
});

const setPrefTableSize = (payload: IPreferencesTableSize) => ({
  type: SET_PREFERENCES_TABLE_SIZE,
  payload
})

const preferencesActions = {
  settingChanged,
  fetchPhoneNumbers,
  getOutgoingPhoneNumbers,
  getOutgoingTextPhoneNumbers,
  updateAppNow,
  clearUpdateNowState,
  checkedForUpdates,
  fetchPhoneNumbersBegin,
  fetchPhoneNumbersSuccess,
  fetchPhoneNumbersFailure,
  toggleTestMic,
  setDefaultAudioInput,
  setDefaultAudioOutput,
  setTextNotificationSound,
  setCallNotificationSound,
  setAudioOutputVolume,
  setAudioInputVolume,
  setOpenPreferences,
  togglePreferencesWindow,
  toggleHistoryAndReportingWindow,
  outgoingPhoneNumberSelected,
  outgoingTextPhoneNumberSelected,
  setPrefTableSize
};

export default preferencesActions;
